import {
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { RadioCardItem, RadioCardLabel, RadioCardRoot } from '@/components/ui/radio-card';
import { Box, Button, chakra, Flex, Text, Textarea, VStack } from '@chakra-ui/react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReasonDialogProps {
  open: boolean;
  title: string;
  description: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmLabel: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (reason: string, choice?: string) => void;
  /**
   * An optional list the reason must also be picked from, above the text. The
   * confirm button waits for both.
   */
  choice?: {
    label: string;
    placeholder: string;
    /**
     * `select` (default) is a native list; `cards` lays every reason out as a
     * radio card with its hint, for a short list the brand should read whole.
     */
    display?: 'select' | 'cards';
    /** Shown beside the cards' label, e.g. « obligatoire ». */
    requiredHint?: string;
    items: { value: string; label: string; description?: string }[];
  };
  testId?: string;
  /** Lines read under the text before confirming: what the gesture spends, what goes with it. */
  notices?: string[];
  /**
   * The text may stay empty: only the choice is required. Meant for a gesture
   * whose picked reason already says enough, with the text as an option.
   */
  reasonOptional?: boolean;
  /**
   * `danger` (default) paints the confirm red, for a gesture taking something
   * back; `neutral` keeps the solid dark button of an ordinary request.
   */
  confirmTone?: 'danger' | 'neutral';
}

/**
 * A confirm-with-a-reason dialog: a textarea the confirm button stays
 * disabled against until it holds something (unless the text is optional and
 * a choice carries the reason), reset every time the dialog opens. Callers resolve every string (title, labels, their own i18n scope)
 * so this one dialog can be shared by gestures that live in unrelated
 * feature trees without either owning the other's key tree.
 */
export const ReasonDialog = ({
  open,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  isLoading,
  onCancel,
  onConfirm,
  choice,
  testId,
  notices = [],
  reasonOptional = false,
  confirmTone = 'danger',
}: ReasonDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState('');
  const reasonId = useId();
  const asCards = choice?.display === 'cards';

  useEffect(() => {
    if (open) {
      setReason('');
      setPicked('');
    }
  }, [open]);

  const ready = (reasonOptional || !!reason.trim()) && (!choice || !!picked);

  return (
    <DialogRoot open={open} onOpenChange={(event) => !event.open && onCancel()}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription fontSize="13px" color="fg.muted" mb="12px">
            {description}
          </DialogDescription>
          {choice && !asCards && (
            <NativeSelectRoot mb="12px">
              <NativeSelectField
                aria-label={choice.label}
                placeholder={choice.placeholder}
                value={picked}
                onChange={(event) => setPicked(event.currentTarget.value)}
                items={choice.items}
              />
            </NativeSelectRoot>
          )}
          {choice && asCards && (
            <RadioCardRoot
              value={picked || null}
              onValueChange={(details) => setPicked(details.value ?? '')}
              size="sm"
              mb="14px"
            >
              <RadioCardLabel fontSize="13px" fontWeight={500} color="dark" mb="6px">
                {choice.label}
                {choice.requiredHint ? (
                  <Text as="span" color="fg.muted" fontWeight={400} ms="4px">
                    {`· ${choice.requiredHint}`}
                  </Text>
                ) : null}
              </RadioCardLabel>
              <VStack align="stretch" gap="6px">
                {choice.items.map((item) => (
                  <RadioCardItem
                    key={item.value}
                    value={item.value}
                    label={item.label}
                    description={item.description}
                    indicatorPlacement="start"
                  />
                ))}
              </VStack>
            </RadioCardRoot>
          )}
          {asCards ? (
            <chakra.label
              htmlFor={reasonId}
              display="block"
              fontSize="13px"
              fontWeight={500}
              color="dark"
              mb="6px"
            >
              {reasonLabel}
            </chakra.label>
          ) : null}
          <Textarea
            id={reasonId}
            variant="outline"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            aria-label={asCards ? undefined : reasonLabel}
            placeholder={reasonPlaceholder}
            rows={asCards ? 3 : 4}
            autoFocus={!asCards}
          />
          {notices.length > 0 && asCards ? (
            <Box
              mt="12px"
              p="10px 12px"
              borderRadius="10px"
              bg="softCream"
              data-testid="reason-dialog-notices"
            >
              {notices.map((notice) => (
                <Text key={notice} fontSize="12.5px" color="ink">
                  {notice}
                </Text>
              ))}
            </Box>
          ) : (
            notices.map((notice) => (
              <Text key={notice} fontSize="12px" color="fg.muted" mt="8px">
                {notice}
              </Text>
            ))
          )}
        </DialogBody>
        <DialogFooter>
          <Flex w="full" justify="flex-end" gap="8px" wrap="wrap">
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              colorPalette={confirmTone === 'danger' ? 'red' : undefined}
              data-tone={confirmTone}
              onClick={() => (choice ? onConfirm(reason.trim(), picked) : onConfirm(reason.trim()))}
              disabled={!ready}
              loading={isLoading}
            >
              {confirmLabel}
            </Button>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
